import {
  ChangeEvent,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import Chevron from 'assets/icons/chevron-sharp.svg?react';
import { EventCollectorProps } from 'hooks/context/EventCollector/types';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import CheckboxEvent from 'hooks/context/EventCollector/events/CheckboxEvent';
import styles from './Checkbox.module.scss';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

export type Props = {
  children: ReactNode;
  className?: string;
  classNameContent?: string;
  classNameContainer?: string;
  dataTest?: string;
  disabled?: boolean;
  error?: string;
  isTouched?: boolean;
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: boolean;
  expand?: boolean;
  pending?: boolean;
};

function Checkbox({
  children,
  className,
  classNameContent,
  classNameContainer,
  dataTest = 'checkbox',
  disabled,
  error,
  isTouched,
  name,
  onChange,
  value,
  expand = false,
  eventCollector,
  pending = false,
}: Props & EventCollectorProps) {
  const { t } = useTranslation('common');
  const { track } = useEventCollector();
  const [expanded, setExpanded] = useState(expand);
  const [expandable, setExpandable] = useState(false);
  const [wasExpanded, setWasExpanded] = useState(false);
  const showError = isTouched && error;

  const handleExpand = useCallback(() => {
    if (!wasExpanded) setWasExpanded(true);

    setExpanded((prev) => !prev);
  }, [wasExpanded]);

  const handleEventCollector = useCallback(
    (checked: boolean) => {
      if (eventCollector?.dontTrack) {
        return;
      }

      track(
        new CheckboxEvent({
          checkboxChecked: checked,
          description: eventCollector?.description ?? name,
        }),
      );
    },
    [eventCollector?.description, eventCollector?.dontTrack, name, track],
  );

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (expandable && !wasExpanded && !expand) {
        handleExpand();
        return;
      }

      if (onChange) {
        onChange(e);
        handleEventCollector(e.target.checked);
      }
    },
    [
      expandable,
      wasExpanded,
      expand,
      onChange,
      handleExpand,
      handleEventCollector,
    ],
  );

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current && containerRef.current.clientHeight > 120) {
      setExpandable(true);
    }
  }, [children]);

  return (
    <div className={classes(className, styles.wrapper)}>
      <label
        className={classes(
          styles.container,
          classNameContainer,
          (expanded || !expandable) && styles.expanded,
          pending && styles.pending,
        )}
        data-test={`${dataTest}-label`}
      >
        <input
          type="checkbox"
          name={name}
          id={name}
          onChange={handleOnChange}
          value={String(value)}
          checked={value}
          data-test={`${dataTest}-input`}
          disabled={disabled || pending}
          className={styles.invisible}
        />
        {!expanded && expandable && <div className={styles.overlay} />}
        <div
          ref={containerRef}
          className={classNameContent}
          data-test={`${dataTest}-content`}
        >
          {children}
        </div>
        <span
          className={classes(styles.checkmark, showError && styles.error)}
        />
      </label>
      {expandable && !expand && (
        <div className={styles['read-more']} onClick={handleExpand}>
          <Chevron className={classes(styles.chevron, expanded && styles.up)} />
          <span className={styles['read-more-label']}>
            {expanded ? t('show_less') : t('show_more')}
          </span>
        </div>
      )}
      {showError && (
        <div className={styles['error-message']}>
          <ErrorMessage errors={error} />
        </div>
      )}
    </div>
  );
}

const CheckboxMemo = memo(Checkbox);

export default CheckboxMemo;
